import React, { useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Image,
  List,
  ListItem,
  ListIcon,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaChartLine, FaCheck, FaRocket, FaTasks, FaUsers } from 'react-icons/fa';
import { LANDING_IMAGES } from '../../constants/landingAssets';

const features = [
  {
    id: 'tasks',
    icon: FaTasks,
    title: 'Task Management',
    tagline: 'Every deliverable, tracked end to end',
    desc: 'Break work into tasks and subtasks with priorities, statuses, and a full audit trail—so nothing slips through the cracks.',
    bullets: ['Subtasks with status history', 'Custom priorities & task types', 'Assignments, due dates & comments'],
    image: LANDING_IMAGES.tasks,
    color: 'blue',
    metric: '100%',
    metricLabel: 'visibility on active work',
  },
  {
    id: 'collaboration',
    icon: FaUsers,
    title: 'Team Collaboration',
    tagline: 'Keep your agency aligned',
    desc: 'Give every teammate clarity on who owns what, with role-based access that scales from small crews to full delivery teams.',
    bullets: ['Admin, editor & viewer roles', 'Project-level assignments', 'Invite flows for growing teams'],
    image: LANDING_IMAGES.collaboration,
    color: 'purple',
    metric: '2–50+',
    metricLabel: 'seats per workspace',
  },
  {
    id: 'projects',
    icon: FaRocket,
    title: 'Project Delivery',
    tagline: 'Client work, beautifully organized',
    desc: 'Run projects from quote to completion with specs, files, timelines, and budgets—all tied back to the client record.',
    bullets: ['Specs & file uploads', 'Quoted & agreed pricing', 'Optional client association'],
    image: LANDING_IMAGES.projects,
    color: 'teal',
    metric: '1 place',
    metricLabel: 'for all project context',
  },
  {
    id: 'analytics',
    icon: FaChartLine,
    title: 'Analytics & Insights',
    tagline: 'Decisions backed by real data',
    desc: 'See quoted value, workload distribution, and delivery progress in dashboards designed for agency operators.',
    bullets: ['Quoted-value reporting', 'Progress & status breakdowns', 'Pro plan analytics dashboard'],
    image: LANDING_IMAGES.analytics,
    color: 'orange',
    metric: 'Pro',
    metricLabel: 'advanced reporting tier',
  },
];

const FeaturesSection: React.FC = () => {
  const [activeId, setActiveId] = useState(features[0].id);
  const active = features.find((f) => f.id === activeId) ?? features[0];

  const muted = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const sectionBg = useColorModeValue('gray.50', 'gray.900');
  const panelBg = useColorModeValue('white', 'gray.800');
  const inactiveBg = useColorModeValue('white', 'white');

  return (
    <Box
      id="features"
      scrollMarginTop="80px"
      py={{ base: 16, md: 24 }}
      bg={sectionBg}
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="-20%"
        right="-10%"
        w="500px"
        h="500px"
        borderRadius="full"
        bgGradient="radial(blue.200, transparent 70%)"
        opacity={0.35}
        filter="blur(60px)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-10%"
        left="-5%"
        w="400px"
        h="400px"
        borderRadius="full"
        bgGradient="radial(purple.200, transparent 70%)"
        opacity={0.3}
        filter="blur(60px)"
        pointerEvents="none"
      />

      <Box position="relative" maxW="7xl" mx="auto" px={{ base: 4, md: 8 }}>
        <VStack spacing={4} textAlign="center" mb={{ base: 10, md: 14 }} maxW="3xl" mx="auto">
          <Badge colorScheme="blue" px={3} py={1} borderRadius="full" textTransform="none">
            Everything you need
          </Badge>
          <Heading size="2xl" lineHeight="shorter">
            Built for how agencies actually deliver
          </Heading>
          <Text fontSize="lg" color={muted} lineHeight="tall">
            Clients, projects, tasks, and team workflows in one connected system—no more jumping between spreadsheets and chat threads.
          </Text>
        </VStack>

        <Grid
          templateColumns={{ base: '1fr', lg: '0.95fr 1.05fr' }}
          gap={{ base: 8, lg: 10 }}
          alignItems="stretch"
        >
          <GridItem>
            <VStack spacing={3} align="stretch">
              {features.map((feature) => {
                const isActive = feature.id === activeId;
                return (
                  <Box
                    key={feature.id}
                    as="button"
                    type="button"
                    textAlign="left"
                    w="full"
                    p={{ base: 4, md: 5 }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={isActive ? `${feature.color}.500` : borderColor}
                    bg={isActive ? `${feature.color}.50` : inactiveBg}
                    boxShadow={isActive ? 'md' : 'none'}
                    transition="all 0.2s ease"
                    onClick={() => setActiveId(feature.id)}
                    _hover={{
                      borderColor: `${feature.color}.300`,
                      transform: 'translateX(4px)',
                    }}
                    _focusVisible={{ outline: '2px solid', outlineColor: `${feature.color}.400` }}
                  >
                    <HStack align="start" spacing={4}>
                      <Flex
                        align="center"
                        justify="center"
                        boxSize={11}
                        minW={11}
                        borderRadius="lg"
                        bg={isActive ? `${feature.color}.500` : `${feature.color}.100`}
                        color={isActive ? 'white' : `${feature.color}.600`}
                        transition="all 0.2s ease"
                      >
                        <Icon as={feature.icon} boxSize={4} />
                      </Flex>
                      <Box flex="1">
                        <Text
                          fontWeight="bold"
                          fontSize="md"
                          color={isActive ? `${feature.color}.700` : undefined}
                          mb={1}
                        >
                          {feature.title}
                        </Text>
                        <Text fontSize="sm" color={muted} noOfLines={isActive ? undefined : 2}>
                          {feature.tagline}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </GridItem>

          <GridItem>
            <Box
              bg={panelBg}
              borderRadius="2xl"
              overflow="hidden"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="xl"
              h="full"
              display="flex"
              flexDirection="column"
            >
              <Box position="relative" h={{ base: '220px', md: '280px' }} flexShrink={0}>
                <Image
                  src={active.image}
                  alt={active.title}
                  w="full"
                  h="full"
                  objectFit="cover"
                  transition="opacity 0.35s ease"
                  key={active.id}
                />
                <Box
                  position="absolute"
                  inset={0}
                  bgGradient="linear(to-t, blackAlpha.700, blackAlpha.200 50%, transparent)"
                />
                <Box position="absolute" bottom={4} left={4} right={4}>
                  <Badge
                    colorScheme={active.color}
                    mb={2}
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    textTransform="none"
                  >
                    {active.title}
                  </Badge>
                  <Heading size="md" color="white" lineHeight="short">
                    {active.tagline}
                  </Heading>
                </Box>

                <Box
                  position="absolute"
                  top={4}
                  right={4}
                  bg={cardBg}
                  px={4}
                  py={3}
                  borderRadius="xl"
                  boxShadow="lg"
                  textAlign="center"
                  minW="100px"
                >
                  <Text fontSize="xl" fontWeight="extrabold" color={`${active.color}.600`} lineHeight="1">
                    {active.metric}
                  </Text>
                  <Text fontSize="xs" color={muted} mt={1}>
                    {active.metricLabel}
                  </Text>
                </Box>
              </Box>

              <Box p={{ base: 6, md: 8 }} flex="1">
                <Text color={muted} lineHeight="tall" mb={6} fontSize="md">
                  {active.desc}
                </Text>
                <List spacing={3}>
                  {active.bullets.map((bullet) => (
                    <ListItem key={bullet} fontSize="sm" color={muted}>
                      <ListIcon as={FaCheck} color={`${active.color}.500`} />
                      {bullet}
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          </GridItem>
        </Grid>

        <Grid
          templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }}
          gap={4}
          mt={{ base: 10, md: 14 }}
        >
          {features.map((feature) => (
            <Box
              key={`pill-${feature.id}`}
              bg={cardBg}
              borderRadius="xl"
              p={4}
              borderWidth="1px"
              textAlign="center"
              transition="all 0.2s ease"
              cursor="pointer"
              onClick={() => setActiveId(feature.id)}
              boxShadow={feature.id === activeId ? 'md' : 'sm'}
              borderColor={feature.id === activeId ? `${feature.color}.400` : borderColor}
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
            >
              <Icon as={feature.icon} boxSize={5} color={`${feature.color}.500`} mb={2} />
              <Text fontSize="sm" fontWeight="semibold">
                {feature.title}
              </Text>
            </Box>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default FeaturesSection;
